import {
  FechaString,
  IAltaAlquiler,
  IBoletin,
  IdCompra,
  IEmail,
  IPago,
  PagoResponse,
  PagoTestResponse,
  SuscripcionBoletin,
} from 'utils/models/interfaces';
import {
  AlquilerBase,
  Compra,
  CompraEntrada,
  IRequestEspectaculo,
  TipoGenericoRequest,
  VentaListado,
} from 'utils/models/types';
import { API_BASE_URL } from './constants';
import { axiosInstance } from './apiUtils';
import axios from 'axios';
import { obtenerError } from 'utils/utilities';

export const espectaculoPath = `${API_BASE_URL}/Espectaculos`;
const registroTallerPath = `${API_BASE_URL}/Talleres`;
const registroAlquilerPath = `${API_BASE_URL}/Alquileres`;
const filtrarAlqPorFechaPath = `${API_BASE_URL}/Alquileres/entreFechas`;
const registroPagoPath = `${API_BASE_URL}/Pagos/create`;
const enviarBoletinlPath = `${API_BASE_URL}/Email/enviarBoletin`;
const individualEmailPath = `${API_BASE_URL}/Email/contacto`;
const registroGeneroPath = `${API_BASE_URL}/Generos/`;
const compraEntradaPath = `${API_BASE_URL}/CompraEntrada/`;
const pagoEntradaPath = `${API_BASE_URL}/CompraEntrada/pago/`;
const aprobarPagoTestPath = `${API_BASE_URL}/CompraEntrada/test/pago/aprobar/`;
const suscripcionBoletinPath = `${API_BASE_URL}/Email/suscribir`;
const enviarMailRestablecerPath = `${API_BASE_URL}/Usuarios/restablecerPassLink`;
const filtrarVentasPorFechaPath = `${API_BASE_URL}/CompraEntrada/ventasPorfecha`;
const logoutPath = `${API_BASE_URL}/Usuarios/logout`;

export const postData = async <T>(
  url: string,
  data: unknown,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void,
  formData?: boolean
): Promise<T | null> => {
  setLoading(true);
  try {
    let response;
    if (formData) {
      response = await axiosInstance.post(url, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      response = await axiosInstance.post<T>(url, data);
    }
    return response.data as T;
  } catch (error: unknown) {
    console.error(`Error de registro en ${url}:`, error);

    if (axios.isAxiosError(error)) {
      // Manejar específicamente el error de timeout en registro de usuarios
      if (url.includes('/Usuarios') && error.code === 'ECONNABORTED') {
        const mensajeTimeout = '¡Registro exitoso! Pero tuvimos un problema al enviar el correo de confirmación. Por favor, comunicate con un administrador.';
        setError(mensajeTimeout);
        // Retornar un objeto simulado para que el frontend trate el registro como exitoso
        return { message: mensajeTimeout } as T;
      }
      return obtenerError(error, setError);
    } else {
      const errorGenerico = 'Error desconocido';
      setError(errorGenerico);
      console.error(errorGenerico, error);
    }
    return null; // Retorna null en caso de error
  } finally {
    setLoading(false);
  }
};

export const registroTaller = async (
  objetoAltaTaller: FormData,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void,
  formData: boolean
) => {
  await postData(registroTallerPath, objetoAltaTaller, setError, setLoading, formData);
};

export const registroAlquiler = async (
  objetoAltaAlquiler: IAltaAlquiler,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  await postData<IAltaAlquiler>(registroAlquilerPath, objetoAltaAlquiler, setError, setLoading);
};

export const registroEspectaculo = async (
  objetoAltaEspectaculo: IRequestEspectaculo,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void,
  formData: boolean,
  formDataObject?: FormData
) => {
  await postData<IRequestEspectaculo>(
    espectaculoPath,
    formData ? formDataObject : objetoAltaEspectaculo,
    setError,
    setLoading,
    formData
  );
};

export const registroPago = async (
  objetoPago: IPago,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  await postData<IPago>(registroPagoPath, objetoPago, setError, setLoading);
};

export const registroBoletin = async (
  objetoAltaBoletin: IBoletin,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void,
  formData: boolean
) => {
  await postData<IBoletin>(enviarBoletinlPath, objetoAltaBoletin, setError, setLoading, formData);
};

export const solicitudFormularios = async (
  tipoSolicitud: 'socio' | 'contacto' | 'taller' | 'alquiler',
  objRequest: IEmail,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  await postData<IEmail>(individualEmailPath, objRequest, setError, setLoading);
};

// Para el envío de fotos como File, se usa formData:
export const crearFormData = (objetoAltaEspectaculo: IRequestEspectaculo): FormData => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(objetoAltaEspectaculo)) {
    if (value instanceof File) {
      formData.append(key, value);
    } else if (typeof value === 'string' || typeof value === 'number') {
      formData.append(key, value === '' ? '' : value.toString());
    } else {
      formData.append(key, JSON.stringify(value));
    }
  }

  return formData;
};

export const sendFormData = async (endpoint: string, formData: FormData) => {
  try {
    const response = await axiosInstance.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error en la solicitud form-data:', error);
    throw error;
  }
};

// Registro de género
export const registroGenero = async (
  objetoAltaGenero: TipoGenericoRequest,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  await postData<TipoGenericoRequest>(registroGeneroPath, objetoAltaGenero, setError, setLoading);
};

export const compraEntrada = async (
  objetoAltaCompra: Compra,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  const result = await postData<CompraEntrada>(compraEntradaPath, objetoAltaCompra, setError, setLoading);
  if (result) {
    return result;
  }
};

export const pagoEntrada = async (
  objetoPago: IdCompra,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
): Promise<PagoResponse | null> => {
  const { IdCompra } = objetoPago;

  const result = await postData<PagoResponse>(`${pagoEntradaPath}${IdCompra}`, objetoPago, setError, setLoading);

  if (result) {
    return result;
  }
  return null;
};

export const aprobarPagoTest = async (
  idCompra: number,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
): Promise<PagoTestResponse | null> => {
  const result = await postData<PagoTestResponse>(`${aprobarPagoTestPath}${idCompra}`, {}, setError, setLoading);

  if (result) {
    return result;
  }

  return null;
};

export const suscripcionBoletin = async (
  objetoSuscripcion: SuscripcionBoletin,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  await postData<string>(suscripcionBoletinPath, objetoSuscripcion, setError, setLoading);
};

export const enviarMailRestablecer = async (
  objetoEmail: SuscripcionBoletin, // es el mismo formato
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  await postData<string>(enviarMailRestablecerPath, objetoEmail, setError, setLoading);
};

export const ventasPorFecha = async (
  objetoRangoFechas: FechaString,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  const result = await postData<VentaListado[]>(filtrarVentasPorFechaPath, objetoRangoFechas, setError, setLoading);

  return result;
};

export const alquileresPorFecha = async (
  objetoRangoFechas: FechaString,
  setError: (message: string) => void,
  setLoading: (state: boolean) => void
) => {
  const result = await postData<AlquilerBase[]>(filtrarAlqPorFechaPath, objetoRangoFechas, setError, setLoading);

  return result;
};

export const logoutUsuario = async (setError: (message: string) => void, setLoading: (state: boolean) => void) => {
  const result = await postData(logoutPath, null, setError, setLoading);

  return result;
};
